import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Globe, CheckCircle2, XCircle, AlertCircle, Loader2, Search, Server, Clock, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface DnsResult {
  server: string;
  location: string;
  city: string;
  country: string;
  status: 'success' | 'error' | 'not_found';
  records: string[];
  ttl?: number;
  responseTime: number;
}

interface RecordResult {
  domain: string;
  recordType: string;
  propagationPercentage: number;
  isFullyPropagated: boolean;
  isConsistent: boolean;
  results: DnsResult[];
  summary: {
    total: number;
    success: number;
    notFound: number;
    errors: number;
  };
}

interface PropagationResponse {
  success: boolean;
  domain: string;
  overallPropagation: number;
  isFullyPropagated: boolean;
  records: RecordResult[];
  checkedAt: string;
  serverCount: number;
  error?: string;
}

const RECORD_TYPES = [
  { id: 'ALL', label: 'All Records', description: 'A, MX, TXT, NS' },
  { id: 'A', label: 'A', description: 'IPv4 Address' },
  { id: 'AAAA', label: 'AAAA', description: 'IPv6 Address' },
  { id: 'MX', label: 'MX', description: 'Mail Exchange' },
  { id: 'TXT', label: 'TXT', description: 'Text Record' },
  { id: 'CNAME', label: 'CNAME', description: 'Canonical Name' },
  { id: 'NS', label: 'NS', description: 'Name Server' },
];

const ALL_RECORD_TYPES = ['A', 'MX', 'TXT', 'NS'];

const getCountryFlag = (country: string): string => {
  const flags: Record<string, string> = {
    'US': '🇺🇸',
    'CA': '🇨🇦',
    'DE': '🇩🇪',
    'GB': '🇬🇧',
    'NL': '🇳🇱',
    'FR': '🇫🇷',
    'RU': '🇷🇺',
    'JP': '🇯🇵',
    'TW': '🇹🇼',
    'CN': '🇨🇳',
    'KR': '🇰🇷',
    'SG': '🇸🇬',
    'AU': '🇦🇺',
    'IN': '🇮🇳',
    'BR': '🇧🇷',
    'AR': '🇦🇷',
    'AE': '🇦🇪',
    'ZA': '🇿🇦',
  };
  return flags[country] || '🌐';
};

export const DnsPropagationChecker = () => {
  const [domain, setDomain] = useState("");
  const [selectedType, setSelectedType] = useState("A");
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<PropagationResponse | null>(null);
  const [activeTab, setActiveTab] = useState("A");

  const handleCheck = async () => {
    if (!domain.trim()) {
      toast.error("Please enter a domain");
      return;
    }

    setIsLoading(true);
    setResults(null);

    try {
      const recordTypes = selectedType === 'ALL' ? ALL_RECORD_TYPES : [selectedType];
      
      const { data, error } = await supabase.functions.invoke('check-dns-propagation', {
        body: { domain: domain.trim(), recordTypes }
      });

      if (error) throw error;

      if (data.success) {
        setResults(data);
        setActiveTab(recordTypes[0]);
        toast.success(`Checked ${data.serverCount} DNS servers globally`);
      } else {
        toast.error(data.error || "Failed to check DNS propagation");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to check DNS propagation");
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'not_found':
        return <AlertCircle className="w-5 h-5 text-amber-500" />;
      default:
        return <XCircle className="w-5 h-5 text-red-500" />;
    }
  };

  const getStatusBgColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'bg-green-500/10';
      case 'not_found':
        return 'bg-amber-500/10';
      default:
        return 'bg-red-500/10';
    }
  };

  const renderResultsTable = (record: RecordResult) => (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b bg-muted/50">
            <th className="text-left py-3 px-4 font-medium text-sm">Status</th>
            <th className="text-left py-3 px-4 font-medium text-sm">Location</th>
            <th className="text-left py-3 px-4 font-medium text-sm">DNS Server</th>
            <th className="text-left py-3 px-4 font-medium text-sm hidden sm:table-cell">Response</th>
            <th className="text-left py-3 px-4 font-medium text-sm">Result</th>
          </tr>
        </thead>
        <tbody>
          {record.results.map((result, idx) => (
            <tr 
              key={idx} 
              className={`border-b last:border-b-0 transition-colors hover:bg-muted/30 ${getStatusBgColor(result.status)}`}
            >
              <td className="py-3 px-4">
                {getStatusIcon(result.status)}
              </td>
              <td className="py-3 px-4">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{getCountryFlag(result.country)}</span>
                  <div>
                    <div className="font-medium text-sm">{result.location}</div>
                    <div className="text-xs text-muted-foreground">{result.city}</div>
                  </div>
                </div>
              </td>
              <td className="py-3 px-4">
                <div className="font-medium text-sm">{result.server}</div>
              </td>
              <td className="py-3 px-4 hidden sm:table-cell">
                <div className="flex items-center gap-1 text-muted-foreground text-sm">
                  <Clock className="w-3.5 h-3.5" />
                  {result.responseTime}ms
                  {result.ttl && (
                    <span className="ml-2 text-xs">TTL: {result.ttl}s</span>
                  )}
                </div>
              </td>
              <td className="py-3 px-4">
                {result.status === 'success' && result.records.length > 0 ? (
                  <div className="space-y-1 max-w-xs">
                    {result.records.slice(0, 2).map((rec, i) => (
                      <div 
                        key={i} 
                        className="text-xs font-mono bg-background/80 px-2 py-1 rounded truncate"
                        title={rec}
                      >
                        {rec.length > 40 ? `${rec.substring(0, 40)}...` : rec}
                      </div>
                    ))}
                    {result.records.length > 2 && (
                      <div className="text-xs text-muted-foreground">
                        +{result.records.length - 2} more
                      </div>
                    )}
                  </div>
                ) : result.status === 'not_found' ? (
                  <span className="text-amber-600 text-sm">No record</span>
                ) : (
                  <span className="text-red-600 text-sm">Failed</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Search Section */}
      <Card className="border-2">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-xl">
            <Globe className="w-6 h-6 text-primary" />
            DNS Propagation Checker
          </CardTitle>
          <CardDescription>
            Check DNS propagation across 24 global servers in real-time
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <Input
                type="text"
                placeholder="Enter domain name (e.g., example.com)"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCheck()}
                className="h-12 text-base"
              />
            </div>
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger className="w-full sm:w-[180px] h-12">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RECORD_TYPES.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    <span className="font-medium">{type.label}</span>
                    {type.id !== 'ALL' && (
                      <span className="text-muted-foreground ml-2 text-xs">({type.description})</span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={handleCheck} disabled={isLoading} className="h-12 px-6">
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Search className="w-5 h-5 mr-2" />
                  Search
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results Section */}
      {results && results.records.length > 0 && (
        <div className="space-y-4">
          {/* Summary Bar */}
          <Card>
            <CardContent className="py-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-6">
                  <div>
                    <div className="text-sm text-muted-foreground">Domain</div>
                    <div className="font-semibold text-lg">{results.domain}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Servers</div>
                    <div className="font-semibold text-lg">{results.serverCount}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge 
                    variant={results.isFullyPropagated ? "default" : "secondary"}
                    className="h-10 px-4 text-sm"
                  >
                    {results.overallPropagation}% Propagated
                  </Badge>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleCheck}
                    disabled={isLoading}
                    className="h-10"
                  >
                    <RefreshCw className={`w-4 h-4 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tabbed Results for Multiple Record Types */}
          {results.records.length > 1 ? (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-4 mb-4">
                {results.records.map((record) => (
                  <TabsTrigger 
                    key={record.recordType} 
                    value={record.recordType}
                    className="relative"
                  >
                    {record.recordType}
                    <Badge 
                      variant={record.isFullyPropagated ? "default" : "secondary"}
                      className="ml-2 text-xs px-1.5 py-0"
                    >
                      {record.propagationPercentage}%
                    </Badge>
                  </TabsTrigger>
                ))}
              </TabsList>
              {results.records.map((record) => (
                <TabsContent key={record.recordType} value={record.recordType}>
                  <Card>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Server className="w-4 h-4" />
                          {record.recordType} Record Results
                        </CardTitle>
                        <div className="flex items-center gap-3 text-sm">
                          <span className="text-green-600">{record.summary.success} resolved</span>
                          <span className="text-amber-600">{record.summary.notFound} not found</span>
                          {record.summary.errors > 0 && (
                            <span className="text-red-600">{record.summary.errors} errors</span>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      {renderResultsTable(record)}
                    </CardContent>
                  </Card>
                </TabsContent>
              ))}
            </Tabs>
          ) : (
            /* Single Record Type View */
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Server className="w-4 h-4" />
                    {results.records[0].recordType} Record Results
                  </CardTitle>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-green-600">{results.records[0].summary.success} resolved</span>
                    <span className="text-amber-600">{results.records[0].summary.notFound} not found</span>
                    {results.records[0].summary.errors > 0 && (
                      <span className="text-red-600">{results.records[0].summary.errors} errors</span>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {renderResultsTable(results.records[0])}
              </CardContent>
            </Card>
          )}

          {/* Footer Info */}
          <div className="text-center text-xs text-muted-foreground py-2">
            Last checked: {new Date(results.checkedAt).toLocaleString()} • 
            Queried {results.serverCount} DNS servers worldwide
          </div>
        </div>
      )}

      {/* Empty State */}
      {!results && !isLoading && (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Globe className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="font-medium text-lg mb-2">Check DNS Propagation</h3>
            <p className="text-muted-foreground text-sm max-w-md mx-auto">
              Enter a domain name and select a record type to check propagation across 24 global DNS servers. Use "All Records" to check A, MX, TXT, and NS simultaneously.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
